import { useMemo } from 'react';
import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    LINE_CHART_PANEL_PREVIEW_IMAGE,
    PANEL_PRIORITIES,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import usePanelDataService from '../../../services/PanelDataService';
import PerformanceChart from '../../charts/performance-chart/PerformanceChart';
import { BuiltInTooltip } from '../../charts/tooltip/types';
import { useMetricColor } from '../../../utilities/ColorUtilities';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { timeRangeFilterId, virtualMachineFilterId } from '../../filters';

const title = t`Virtual Machine Network I/O`;

const dataSource = {
    url: '/query/chart',
    params: {
        type: 'server',
        metrics: ['vmNetRxbps', 'vmNetTxbps'],
    },
};

export function useChartConfig() {
    const networkInColor = useMetricColor('networkIn');
    const networkOutColor = useMetricColor('networkOut');

    return useMemo(
        () => ({
            yAxis: [
                {
                    title: {
                        text: t`Utilization (Bits/s)`,
                    },
                },
            ],
            chartType: 'spline',
            tooltip: BuiltInTooltip,
            showGranularity: true,
            chartTrends: [
                {
                    formatter: FORMATTER_TYPES.BITRATE,
                    forceDecimals: true,
                    chartType: 'spline',
                    key: 'vmNetRxbps',
                    label: t`In`,
                    color: networkInColor,
                    multiplier: 1,
                },
                {
                    formatter: FORMATTER_TYPES.BITRATE,
                    forceDecimals: true,
                    chartType: 'spline',
                    key: 'vmNetTxbps',
                    label: t`Out`,
                    color: networkOutColor,
                    multiplier: 1,
                },
            ],
        }),
        [networkInColor, networkOutColor]
    );
}

function VirtualMachineNetworkUtilizationPanel() {
    const data = usePanelDataService(dataSource);
    const chartConfig = useChartConfig();
    const chartData = data?.data?.[0];

    if (!data) return null;

    return <PerformanceChart chartDef={chartConfig} data={chartData} />;
}

VirtualMachineNetworkUtilizationPanel.propTypes = panelPropTypes;

VirtualMachineNetworkUtilizationPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    contentHeight: 175,
    minContentHeight: 175,
};

export default Panel({
    typeAlias: 'VmwareVmNetworkThroughputTrend',
    catalogEntry: {
        category: PANEL_CATEGORIES.VIRTUAL_MACHINE,
        priority: PANEL_PRIORITIES.CHART,
        title,
        description: t`Network utilization trend for a selected virtual machine (VM)`,
        previewImage: LINE_CHART_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId, virtualMachineFilterId],
        optional: [],
    },
})(VirtualMachineNetworkUtilizationPanel);
